import { EMULATED_TARGETS, flashSizeBytes, mergeImages, type FlashPlan } from "idfweb-build";
import { chipNameOfTarget } from "idfweb-chip";
import type { EmulatorEvent, EmulatorInput } from "idfweb-runtime-protocol";
import type { Task } from "idfweb-tasks";

import type { Board, BoardEvents, ConnectContext, Connector, Disk, RememberedBoard } from "./board.ts";

/**
 * The emulator (omars-espemu) as a board: a guest process in the runtime
 * worker, whose console is the chip's UART. The page's README describes it.
 */

export interface EmulatorDeps {
    /** Start a run of `image` on `target`, and return the port its console is on. */
    run(target: string, image: Uint8Array, flashBytes: number): MessagePort;
}

export function emulatorConnector(deps: EmulatorDeps): Connector {
    return {
        id: "emulator",
        label: "Emulator",
        unavailable: undefined,
        targets: EMULATED_TARGETS,
        connect: async (context) => connectEmulator(deps, context),
        reconnect: async (_remembered, context) =>
            EMULATED_TARGETS.includes(context.target()) ? connectEmulator(deps, context) : undefined,
    };
}

function connectEmulator(deps: EmulatorDeps, context: ConnectContext): Board {
    const target = context.target();
    // The menu offers it only for these, but the target may change while the menu is open.
    if (!EMULATED_TARGETS.includes(target)) {
        throw new Error(`The emulator does not run ${chipNameOfTarget(target)} builds.`);
    }
    return new EmulatorBoard(deps, context.events, target);
}

class EmulatorBoard implements Board {
    readonly ready = true;
    /** Its chip is the project's target, which the project keeps. */
    readonly remembered: RememberedBoard = { connector: "emulator" };
    readonly #deps: EmulatorDeps;
    readonly #events: BoardEvents;
    #chip: string;
    /** The last image run, which a reset runs again from power-on. */
    #last: { plan: FlashPlan; image: Uint8Array } | undefined;
    #run: EmulatorRun | undefined;

    constructor(deps: EmulatorDeps, events: BoardEvents, chip: string) {
        this.#deps = deps;
        this.#events = events;
        this.#chip = chip;
    }

    get label(): string {
        return `${chipNameOfTarget(this.#chip)} emulator`;
    }

    async flash(plan: FlashPlan, task: Task): Promise<void> {
        if (!EMULATED_TARGETS.includes(plan.chip)) {
            throw new Error(`The emulator does not run ${chipNameOfTarget(plan.chip)} builds.`);
        }
        const image = mergeImages(plan);
        this.#last = { plan, image };
        await this.#start();
        task.progress(image.length, { total: image.length, detail: "started" });
        if (this.#chip !== plan.chip) {
            this.#chip = plan.chip;
            this.#events.changed();
        }
    }

    async reset(): Promise<void> {
        if (this.#last === undefined) throw new Error("Nothing runs in the emulator yet. Press Run first.");
        await this.#start();
    }

    async write(bytes: Uint8Array): Promise<void> {
        if (this.#run === undefined) throw new Error("Nothing runs in the emulator. Press Run or Reset first.");
        this.#run.send({ input: bytes });
    }

    /**
     * Its flash is the image it last ran, as the build wrote it, and erased
     * after the image's end: what the running app wrote is not in it.
     */
    disks(): Disk[] {
        return [{ id: "flash", kind: "internal-nor", label: "Flash", size: this.#flashSize(), eraseSize: 0x1000 }];
    }

    async readDisk(disk: string, offset: number, length: number): Promise<Uint8Array> {
        const image = this.#disk(disk, offset, length);
        const bytes = new Uint8Array(length).fill(0xff);
        bytes.set(image.subarray(offset, offset + length));
        return bytes;
    }

    /** Write into the image and run it again from power-on, as a board resets after a write. */
    async writeDisk(disk: string, offset: number, data: Uint8Array): Promise<void> {
        let image = this.#disk(disk, offset, data.length);
        if (offset + data.length > image.length) {
            image = new Uint8Array(offset + data.length).fill(0xff);
            image.set(this.#last!.image);
            this.#last!.image = image;
        }
        image.set(data, offset);
        await this.#start();
    }

    #flashSize(): number | undefined {
        if (this.#last === undefined) return undefined;
        return Math.max(flashSizeBytes(this.#last.plan.flashSize), this.#last.image.length);
    }

    #disk(disk: string, offset: number, length: number): Uint8Array {
        const size = this.#flashSize();
        if (size === undefined) throw new Error("Nothing runs in the emulator yet. Press Run first.");
        if (disk !== "flash") throw new Error(`The emulator has no disk "${disk}".`);
        if (offset < 0 || length < 0 || offset + length > size) {
            throw new Error(`0x${offset.toString(16)}+${length} is outside the flash.`);
        }
        return this.#last!.image;
    }

    async close(): Promise<void> {
        this.#run?.stop();
        this.#run = undefined;
    }

    /** Stop the current run and start the last image from power-on; throws if it cannot start. */
    async #start(): Promise<void> {
        const { plan, image } = this.#last!;
        this.#run?.stop();
        const port = this.#deps.run(plan.chip, image.slice(), flashSizeBytes(plan.flashSize));
        const run = new EmulatorRun(port, (bytes) => this.#events.data(bytes));
        run.exited = (text) => {
            if (this.#run !== run) return;
            this.#run = undefined;
            this.#events.data(new TextEncoder().encode(`\r\n${text}\r\n`));
        };
        this.#run = run;
        try {
            await run.started;
        } catch (e) {
            if (this.#run === run) this.#run = undefined;
            throw e;
        }
    }
}

/** One emulator process, from the page's side of its port. */
class EmulatorRun {
    /** The run ended by itself; `text` says how, for the console. */
    exited: (text: string) => void = () => {};
    /** Settles when the emulator is running, or rejects with why it could not start. */
    readonly started: Promise<void>;
    readonly #port: MessagePort;
    #start!: { resolve: () => void; reject: (e: Error) => void };
    #log = "";
    #stopped = false;

    constructor(port: MessagePort, output: (bytes: Uint8Array) => void) {
        this.#port = port;
        this.started = new Promise((resolve, reject) => (this.#start = { resolve, reject }));
        let running = false;
        port.onmessage = (message: MessageEvent<EmulatorEvent>) => {
            const event = message.data;
            if ("started" in event) {
                running = true;
                this.#start.resolve();
            } else if ("output" in event) output(event.output);
            else if ("log" in event) this.#log = (this.#log + event.log).slice(-2000);
            else if (!running) this.#start.reject(new Error(ended(event, this.#log)));
            else if (!this.#stopped) this.exited(ended(event, this.#log));
        };
        port.start();
    }

    send(input: EmulatorInput): void {
        if (!this.#stopped) this.#port.postMessage(input);
    }

    stop(): void {
        this.send({ stop: true });
        this.#start.reject(new Error("The emulator was stopped before it started."));
        this.#stopped = true;
        this.#port.close();
    }
}

/** What the console says when the emulator stops by itself. */
function ended(event: Extract<EmulatorEvent, { exited: number }>, log: string): string {
    if (event.failure !== undefined) return `The emulator could not run: ${event.failure}`;
    // Its own messages carry ANSI colours, which the terminal shows as they are.
    const last = log.trimEnd().split("\n").slice(-3).join("\r\n");
    return `The emulator stopped with exit code ${event.exited}.${last === "" ? "" : `\r\n${last}`}`;
}
